import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Plus, ArrowRight, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, StatusBadge, EmptyState, Card } from "@/components/common";

export default function Clients() {
  const { companyParam, companyId, companies } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["clients", companyId],
    queryFn: async () => (await api.get("/clients", { params: companyParam })).data,
  });
  const coName = (id) => companies.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="clients-page">
      <PageHeader
        title="Clients"
        subtitle="Every client and their jobs, tracked with a full activity timeline."
        actions={<button onClick={() => setOpen(true)} data-testid="new-client-btn" className="flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-dark"><Plus className="w-4 h-4" strokeWidth={2.5} /> New Client</button>}
      />
      {isLoading ? <div className="text-sm text-ink-muted">Loading…</div> : clients.length === 0 ? (
        <EmptyState icon={Building2} title="No clients yet" hint="Clients are created automatically from directives, or add one manually." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <button key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-card-${c.id}`} className="text-left bg-white border border-border hover:border-ink hover:-translate-y-[1px] hover:shadow-sm transition-all p-4 group">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 bg-ink text-white flex items-center justify-center font-heading font-bold">{c.name[0]}</div>
                <StatusBadge value={c.status} />
              </div>
              <h3 className="font-heading text-base font-semibold text-ink mt-3">{c.name}</h3>
              <p className="text-xs text-ink-muted">{coName(c.company_id)}</p>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <span className="text-xs font-mono text-ink-faint">{c.job_count} job(s)</span>
                <ArrowRight className="w-4 h-4 text-ink-faint group-hover:text-brand group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>
          ))}
        </div>
      )}
      {open && <NewClientDialog onClose={() => setOpen(false)} companies={companies} defaultCompany={companyId} />}
    </div>
  );
}

function NewClientDialog({ onClose, companies, defaultCompany }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ company_id: defaultCompany !== "all" ? defaultCompany : companies[0]?.id || "", name: "", contact_email: "", contact_phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    setBusy(true);
    try {
      await api.post("/clients", form);
      toast.success("Client created");
      qc.invalidateQueries({ queryKey: ["clients"] });
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const F = "w-full border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white border-2 border-brand">
        <div className="flex items-center justify-between px-5 py-3 bg-ink text-white">
          <h2 className="font-heading font-bold">New Client</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <select className={F} value={form.company_id} onChange={set("company_id")}>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          <input className={F} placeholder="Client name *" value={form.name} onChange={set("name")} data-testid="client-name-input" />
          <input className={F} placeholder="Contact email" value={form.contact_email} onChange={set("contact_email")} />
          <input className={F} placeholder="Contact phone" value={form.contact_phone} onChange={set("contact_phone")} />
          <textarea className={F} rows={2} placeholder="Notes" value={form.notes} onChange={set("notes")} />
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-border">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="save-client-btn" className="flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-medium disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Create</button>
          </div>
        </div>
      </div>
    </div>
  );
}
